import React, { useMemo, useEffect, useState, useCallback } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import userService from '../../services/userService';
import { useToast } from '../../hooks/useToast';
import DataTable from "../../components/common/DataTable";
import Button from '../../components/common/Button';
import { useInfiniteQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faPlus } from '@fortawesome/free-solid-svg-icons';

const PAGE_LIMIT = 20;

const EMPTY_FORM = { name: '', email: '', password: '', role: 'investor', status: 'active' };

function UserFormModal({ open, onClose, onSubmit, initialData, isEdit }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setForm(initialData || EMPTY_FORM);
  }, [open, initialData]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onSubmit(form);
      onClose();
    } catch {
      // parent handles toast
    } finally {
      setSubmitting(false);
    }
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
        <h3 style={{ marginTop: 0 }}>{isEdit ? 'Edit User' : 'Create User'}</h3>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Name</label>
            <input required value={form.name} onChange={set('name')}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Email</label>
            <input required type="email" value={form.email} onChange={set('email')}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db' }} />
          </div>
          {!isEdit && (
            <div>
              <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Password</label>
              <input required type="password" value={form.password} onChange={set('password')} minLength={8}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db' }} />
            </div>
          )}
          <div>
            <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Role</label>
            <select value={form.role} onChange={set('role')}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db' }}>
              <option value="superadmin">Superadmin</option>
              <option value="manager">Manager</option>
              <option value="artist">Artist</option>
              <option value="investor">Investor</option>
            </select>
          </div>
          {isEdit && (
            <div>
              <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Status</label>
              <select value={form.status} onChange={set('status')}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db' }}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function SuperAdminUserManagement() {
  const { showToast } = useToast();
  const [search, setSearch] = useState('');
  const [role, setRole] = useState('');
  const [status, setStatus] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);

  const columns = [
    { key: "user_id", label: "ID" },
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    {
      key: "role",
      label: "Role",
      render: (value) => (
        <span className={`badge ${value}`}>{value}</span>
      ),
    },
  ];

  const usersQuery = useInfiniteQuery({
    queryKey: ['superadmin-users', search, role, status],
    initialPageParam: undefined,
    queryFn: ({ pageParam }) => userService.getAllUsers({
      limit: PAGE_LIMIT,
      cursor: pageParam,
      search: search || undefined,
      role: role || undefined,
      status: status || undefined,
      fields: 'user_id,client_id,name,email,role,status,created_at',
    }),
    getNextPageParam: (lastPage) => {
      const pagination = lastPage?.data?.data?.pagination;
      return pagination?.has_more ? pagination?.next_cursor : undefined;
    },
  });

  const users = useMemo(() => (
    usersQuery.data?.pages?.flatMap((page) => page?.data?.data?.items || []) || []
  ), [usersQuery.data]);

  useEffect(() => {
    if (usersQuery.error) {
      showToast('Failed to fetch users', 500);
    }
  }, [usersQuery.error, showToast]);

  const handleCreate = useCallback(async (form) => {
    try {
      await userService.createUser({ name: form.name, email: form.email, password: form.password, role: form.role });
      showToast('User created', 'success');
      usersQuery.refetch();
    } catch (err) {
      showToast(err?.response?.data?.message || 'Failed to create user', 'error');
      throw err;
    }
  }, [showToast, usersQuery]);

  const handleEdit = useCallback(async (form) => {
    try {
      await userService.updateUser(editUser.user_id, { name: form.name, email: form.email, role: form.role, status: form.status });
      showToast('User updated', 'success');
      setEditUser(null);
      usersQuery.refetch();
    } catch (err) {
      showToast(err?.response?.data?.message || 'Failed to update user', 'error');
      throw err;
    }
  }, [editUser, showToast, usersQuery]);

  const openEdit = useCallback(async (user) => {
    try {
      const res = await userService.getUser(user.user_id);
      const u = res?.data?.data;
      setEditUser({ ...u, user_id: user.user_id });
    } catch {
      showToast('Failed to load user details', 'error');
    }
  }, [showToast]);

  return (
    <DashboardLayout>
      <div style={{ padding: '0 20px', marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>Users</h3>
          <Button variant="primary" onClick={() => setModalOpen(true)}>
            <FontAwesomeIcon icon={faPlus} style={{ marginRight: 6 }} />Create User
          </Button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, alignItems: 'end' }}>
          <div>
            <label htmlFor="users-search" style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Search</label>
            <input
              id="users-search"
              type="text"
              placeholder="Name or email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db' }}
            />
          </div>
          <div>
            <label htmlFor="users-role" style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Role</label>
            <select
              id="users-role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db' }}
            >
              <option value="">All roles</option>
              <option value="superadmin">Superadmin</option>
              <option value="manager">Manager</option>
              <option value="artist">Artist</option>
              <option value="investor">Investor</option>
            </select>
          </div>
          <div>
            <label htmlFor="users-status" style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Status</label>
            <select
              id="users-status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db' }}
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
        </div>
      </div>
      <DataTable
        columns={columns}
        data={users}
        loading={usersQuery.isLoading}
        emptyMessage="No users found."
        actions={(user) => (
          <button
            className="btn-icon"
            onClick={() => openEdit(user)}
          >
            <FontAwesomeIcon icon={faEdit} />
          </button>
        )}
      />
      <div style={{ padding: '12px 20px 20px', display: 'flex', justifyContent: 'center' }}>
        {usersQuery.hasNextPage ? (
          <button
            type="button"
            onClick={() => usersQuery.fetchNextPage()}
            disabled={usersQuery.isFetchingNextPage}
            style={{
              border: 'none',
              borderRadius: 8,
              padding: '10px 14px',
              fontWeight: 600,
              cursor: usersQuery.isFetchingNextPage ? 'default' : 'pointer',
              background: '#111827',
              color: '#f9fafb',
              opacity: usersQuery.isFetchingNextPage ? 0.75 : 1,
            }}
          >
            {usersQuery.isFetchingNextPage ? 'Loading more...' : 'Load more users'}
          </button>
        ) : null}
      </div>

      <UserFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleCreate}
        isEdit={false}
      />
      <UserFormModal
        open={!!editUser}
        onClose={() => setEditUser(null)}
        onSubmit={handleEdit}
        initialData={editUser}
        isEdit
      />
    </DashboardLayout>
  );
}
